import { useCallback } from 'react';

import { Row, Table } from '@tanstack/react-table';
import { UseFormReturn } from 'react-hook-form';

import { parseFormNumber } from '@/helpers/finance';

import {
  asSubRow,
  getBillFieldName,
  isNonDataSubRow,
} from '../../components/generate-bills-table/bill-row.helpers';
import {
  GenerateBillAddedProjectListItem,
  GenerateBillListItem,
  GenerateBillMemberOverheadListItem,
  GenerateBillMemberProjectListItem,
  GenerateBillMemberTimeOffListItem,
  GenerateBillSalaryAdjustmentListItem,
  GenerateBillSubRowType,
} from '../../types';
import {
  calculateLineAmount,
  calculateSalaryAdjustment,
  getSalaryAdjustmentId,
  sumReconciledLineAmounts,
} from '../../utils';
import { GenerateBillsTableFormValues } from '../../validations';

interface UseBillMembersPayloadParams {
  form: UseFormReturn<GenerateBillsTableFormValues>;
  table: Table<GenerateBillListItem>;
  selectedRowObjects: Row<GenerateBillListItem>[];
}

/** The selected sub-rows of one member, split by type with form edits applied. */
interface MemberSubRowValues {
  projects: GenerateBillMemberProjectListItem[];
  addedProjects: GenerateBillAddedProjectListItem[];
  timeOffs: GenerateBillMemberTimeOffListItem[];
  overheads: GenerateBillMemberOverheadListItem[];
}

/**
 * Builds the member list the generate modal submits, overlaying the current form values onto each
 * selected row.
 *
 * Returns a callback rather than a value because it must read the LATEST form state at click
 * time — reading during render would submit whatever was typed as of the last render.
 */
export const useBillMembersPayload = ({
  form,
  table,
  selectedRowObjects,
}: UseBillMembersPayloadParams): (() => GenerateBillListItem[]) => {
  const { getValues } = form;

  // Read a leaf's edited value by its id-keyed field path (no index bookkeeping).
  const readField = useCallback(
    <TValue>(subRow: Row<GenerateBillListItem>, name: string): TValue | undefined =>
      getValues(getBillFieldName(subRow, name) as never) as TValue | undefined,
    [getValues]
  );

  const collectSubRowValues = useCallback(
    (memberRow: Row<GenerateBillListItem>): MemberSubRowValues => {
      const values: MemberSubRowValues = {
        projects: [],
        addedProjects: [],
        timeOffs: [],
        overheads: [],
      };

      for (const subRow of memberRow.subRows) {
        // Button rows carry no data, and the adjustment is recomputed below from the submitted
        // values rather than read off the row.
        if (isNonDataSubRow(subRow)) continue;
        if (!selectedRowObjects.includes(subRow)) continue;

        const subRowOriginal = asSubRow(subRow);

        switch (subRowOriginal.type) {
          case GenerateBillSubRowType.OVERHEAD: {
            const description = String(
              readField<string>(subRow, 'description') ?? subRowOriginal.description
            );
            // An unnamed overhead would produce a blank QuickBooks line, so drop it. The
            // generate-disabled check normally stops the user before this point.
            if (description.trim() === '') break;

            values.overheads.push({
              ...subRowOriginal,
              description,
              effectiveRate: parseFormNumber(
                readField<number>(subRow, 'effectiveRate') ?? subRowOriginal.effectiveRate
              ),
            });
            break;
          }

          case GenerateBillSubRowType.ADDED_PROJECT: {
            const rawProjectId = readField<number | null>(subRow, 'projectId');
            const projectId = rawProjectId ? parseInt(String(rawProjectId), 10) : null;
            // A custom line with no project chosen has nothing to bill against.
            if (!projectId) break;

            const hours = parseFormNumber(readField<number>(subRow, 'hours') ?? 0);
            const effectiveRate = parseFormNumber(readField<number>(subRow, 'effectiveRate') ?? 0);
            values.addedProjects.push({
              ...subRowOriginal,
              projectId,
              hours,
              effectiveRate,
              total: calculateLineAmount({ hours, rate: effectiveRate }),
            });
            break;
          }

          case GenerateBillSubRowType.TIME_OFF: {
            const hours = readField<number>(subRow, 'hours') ?? subRowOriginal.hours;
            const effectiveRate =
              readField<number>(subRow, 'effectiveRate') ?? subRowOriginal.effectiveRate;
            values.timeOffs.push({
              ...subRowOriginal,
              hours,
              effectiveRate,
              total: calculateLineAmount({ hours, rate: effectiveRate }),
            });
            break;
          }

          case GenerateBillSubRowType.PROJECT: {
            const hours = readField<number>(subRow, 'hours') ?? subRowOriginal.hours;
            const effectiveRate =
              readField<number>(subRow, 'effectiveRate') ?? subRowOriginal.effectiveRate;
            values.projects.push({
              ...subRowOriginal,
              hours,
              effectiveRate,
              billBy:
                readField<typeof subRowOriginal.billBy>(subRow, 'billBy') ?? subRowOriginal.billBy,
              total: calculateLineAmount({ hours, rate: effectiveRate }),
            });
            break;
          }
        }
      }

      return values;
    },
    [readField, selectedRowObjects]
  );

  return useCallback(() => {
    const memberRowsToBill = new Map<number, Row<GenerateBillListItem>>();

    // A member is billable if any of its sub-rows is selected...
    selectedRowObjects.forEach(rowObject => {
      const parentRow = rowObject.getParentRow();
      if (!parentRow) return;
      memberRowsToBill.set(parentRow.original.member.id, parentRow);
    });

    // ...or if it is billed as a monthly salary, which stands on its own even with nothing
    // selected. Without this the salary would silently never reach the payload.
    table.getCoreRowModel().rows.forEach(memberRow => {
      if (!memberRow.original.isMonthlySalaryEnabled) return;
      if (memberRowsToBill.has(memberRow.original.member.id)) return;
      memberRowsToBill.set(memberRow.original.member.id, memberRow);
    });

    return Array.from(memberRowsToBill.values()).map(memberRow => {
      const member = memberRow.original;
      const { projects, addedProjects, timeOffs, overheads } = collectSubRowValues(memberRow);

      // Recomputed from the values actually being submitted rather than the row snapshot — the
      // user may have edited hours/rates or changed the selection since the row was composed.
      const salaryAdjustment: GenerateBillSalaryAdjustmentListItem | null =
        member.isMonthlySalaryEnabled
          ? {
              type: GenerateBillSubRowType.SALARY_ADJUSTMENT,
              id: getSalaryAdjustmentId(member.id),
              monthlySalaryAmount: member.monthlySalaryAmount,
              adjustmentAmount: calculateSalaryAdjustment({
                monthlySalaryAmount: member.monthlySalaryAmount,
                // Summed from each line's *rounded* amount — the same figure the request mapper
                // sends for that line — so `Σ(lines) + adjustment` lands exactly on the salary.
                reconciledLinesTotal: sumReconciledLineAmounts(
                  [...projects, ...addedProjects, ...timeOffs].map(line => ({
                    hours: line.hours,
                    rate: line.effectiveRate,
                  }))
                ),
              }),
              currency: member.currency,
            }
          : null;

      return {
        ...member,
        memberProjects: projects,
        memberAddedProjects: addedProjects,
        memberTimeOffs: timeOffs,
        memberOverheads: overheads,
        memberSalaryAdjustment: salaryAdjustment,
        memberSubRows: [
          ...projects,
          ...addedProjects,
          ...timeOffs,
          ...overheads,
          ...(salaryAdjustment ? [salaryAdjustment] : []),
        ],
      };
    });
  }, [collectSubRowValues, selectedRowObjects, table]);
};
